import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Building2, Users, Briefcase, FileText, Package, Receipt,
  Phone, Mail, Calendar, CreditCard, Trash2, AlertTriangle, DollarSign,
  Ban, Key, RefreshCw, Shield, UserX, Check, X, Clock
} from 'lucide-react';
import { supabase } from '../supabase';
import { formatDate, formatCurrency, getMonthlyRevenue } from '../utils/commissionCalc';
import { logAdminAction } from '../utils/auditLog';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Building2 },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'team', label: 'Team', icon: Users },
  { id: 'customers', label: 'Customers', icon: Users },
  { id: 'jobs', label: 'Jobs', icon: Briefcase },
  { id: 'invoices', label: 'Invoices', icon: Receipt },
  { id: 'activity', label: 'Activity', icon: Calendar },
  { id: 'danger', label: 'Danger Zone', icon: AlertTriangle },
];

export default function CompanyViewer({ companyId, onBack }) {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [tabData, setTabData] = useState({});
  const [tabLoading, setTabLoading] = useState({});

  // Action states
  const [actionLoading, setActionLoading] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [purgeOwner, setPurgeOwner] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');

  // Get admin email from localStorage
  const getAdminEmail = () => {
    try {
      const saved = localStorage.getItem('workvan_internal_auth');
      if (saved) {
        const { email } = JSON.parse(saved);
        return email;
      }
    } catch (e) {}
    return null;
  };

  useEffect(() => {
    if (companyId) {
      loadCompany();
    }
  }, [companyId]);

  useEffect(() => {
    if (company && activeTab !== 'overview' && activeTab !== 'danger') {
      loadTabData(activeTab);
    }
  }, [activeTab, company]);

  const loadCompany = async () => {
    try {
      const [companyRes, membersRes, usersRes] = await Promise.all([
        supabase.from('companies').select('*').eq('id', companyId).single(),
        supabase.from('user_companies').select('*').eq('company_id', companyId),
        supabase.from('user_profiles').select('*')
      ]);

      if (companyRes.error) throw companyRes.error;

      const companyData = companyRes.data;
      const membersData = membersRes.data || [];
      const usersData = usersRes.data || [];

      const userMap = {};
      usersData.forEach(u => { userMap[u.id] = u; });

      const members = membersData.map(m => ({
        ...m,
        user: userMap[m.user_id] || null
      }));

      const owner = members.find(m => m.role === 'owner');

      setCompany({
        ...companyData,
        members,
        owner: owner?.user || null
      });

      loadOverviewCounts(companyId);
    } catch (error) {
      console.error('Error loading company:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOverviewCounts = async (cId) => {
    try {
      const [customersRes, jobsRes, quotesRes, invoicesRes] = await Promise.all([
        supabase.from('customers').select('id', { count: 'exact', head: true }).eq('company_id', cId),
        supabase.from('jobs').select('id', { count: 'exact', head: true }).eq('company_id', cId),
        supabase.from('quotes').select('id', { count: 'exact', head: true }).eq('company_id', cId),
        supabase.from('invoices').select('id', { count: 'exact', head: true }).eq('company_id', cId),
      ]);

      setTabData(prev => ({
        ...prev,
        _counts: {
          customers: customersRes.count || 0,
          jobs: jobsRes.count || 0,
          quotes: quotesRes.count || 0,
          invoices: invoicesRes.count || 0,
        }
      }));
    } catch (error) {
      console.error('Error loading counts:', error);
    }
  };

  const loadTabData = async (tab) => {
    if (tabData[tab]) return;
    setTabLoading(prev => ({ ...prev, [tab]: true }));

    try {
      let data;
      switch (tab) {
        case 'team':
          data = company.members;
          break;

        case 'billing':
          // Load billing-related data
          const billingRes = await supabase
            .from('billing_credits')
            .select('*')
            .eq('company_id', companyId)
            .order('created_at', { ascending: false });
          data = { credits: billingRes.data || [] };
          break;

        case 'customers':
          const customersRes = await supabase
            .from('customers')
            .select('id, name, phone, email, created_at')
            .eq('company_id', companyId)
            .order('created_at', { ascending: false })
            .limit(100);
          data = customersRes.data || [];
          break;

        case 'jobs':
          const jobsRes = await supabase
            .from('jobs')
            .select('id, job_number, status, scheduled_date, completed_date, total, customer_id')
            .eq('company_id', companyId)
            .order('created_at', { ascending: false })
            .limit(50);
          const jobCustomerIds = [...new Set((jobsRes.data || []).map(j => j.customer_id).filter(Boolean))];
          let jobCustomerMap = {};
          if (jobCustomerIds.length > 0) {
            const { data: jcData } = await supabase
              .from('customers')
              .select('id, name')
              .in('id', jobCustomerIds);
            (jcData || []).forEach(c => { jobCustomerMap[c.id] = c.name; });
          }
          data = (jobsRes.data || []).map(j => ({
            ...j,
            customer_name: jobCustomerMap[j.customer_id] || '-'
          }));
          break;

        case 'invoices':
          const invoicesRes = await supabase
            .from('invoices')
            .select('id, invoice_number, status, total, due_date, customer_id, created_at')
            .eq('company_id', companyId)
            .order('created_at', { ascending: false })
            .limit(50);
          const invCustomerIds = [...new Set((invoicesRes.data || []).map(i => i.customer_id).filter(Boolean))];
          let invCustomerMap = {};
          if (invCustomerIds.length > 0) {
            const { data: icData } = await supabase
              .from('customers')
              .select('id, name')
              .in('id', invCustomerIds);
            (icData || []).forEach(c => { invCustomerMap[c.id] = c.name; });
          }
          data = (invoicesRes.data || []).map(i => ({
            ...i,
            customer_name: invCustomerMap[i.customer_id] || '-'
          }));
          break;

        case 'activity':
          const events = [];

          // Company created event
          if (company.created_at) {
            events.push({
              type: 'company',
              title: 'Company Created',
              description: company.name,
              date: company.created_at,
            });
          }

          // Member joined events
          if (company.members && company.members.length > 0) {
            company.members.forEach(m => {
              events.push({
                type: 'team',
                title: 'Member Joined',
                description: `${m.user?.full_name || m.user?.email || 'Unknown'} (${m.role})`,
                date: m.joined_at || m.created_at,
              });
            });
          }

          // Recent jobs
          const actJobsRes = await supabase
            .from('jobs')
            .select('id, job_number, status, created_at')
            .eq('company_id', companyId)
            .order('created_at', { ascending: false })
            .limit(20);
          (actJobsRes.data || []).forEach(j => {
            events.push({
              type: 'job',
              title: 'Job Created',
              description: `#${j.job_number || 'N/A'} - ${j.status || 'unknown'}`,
              date: j.created_at,
            });
          });

          // Recent invoices
          const actInvRes = await supabase
            .from('invoices')
            .select('id, invoice_number, status, total, created_at')
            .eq('company_id', companyId)
            .order('created_at', { ascending: false })
            .limit(20);
          (actInvRes.data || []).forEach(inv => {
            events.push({
              type: 'invoice',
              title: 'Invoice Created',
              description: `#${inv.invoice_number || 'N/A'} - ${inv.status || 'draft'}${inv.total != null ? ` - ${formatCurrency(inv.total)}` : ''}`,
              date: inv.created_at,
            });
          });

          // Admin actions
          const actAdminRes = await supabase
            .from('admin_actions')
            .select('*')
            .eq('target_id', companyId)
            .order('created_at', { ascending: false })
            .limit(20);
          (actAdminRes.data || []).forEach(a => {
            events.push({
              type: 'admin',
              title: a.action_type || 'Admin Action',
              description: `${a.admin_email || 'Unknown admin'}${a.details ? ` - ${a.details}` : ''}`,
              date: a.created_at,
            });
          });

          // Sort by date descending
          events.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
          data = events;
          break;

        default:
          data = [];
      }

      setTabData(prev => ({ ...prev, [tab]: data }));
    } catch (error) {
      console.error(`Error loading ${tab}:`, error);
      setTabData(prev => ({ ...prev, [tab]: [] }));
    } finally {
      setTabLoading(prev => ({ ...prev, [tab]: false }));
    }
  };

  // Admin Actions
  const cancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel this subscription? This will set status to "canceled".')) return;
    setActionLoading('cancel');
    try {
      const { error } = await supabase
        .from('companies')
        .update({ subscription_status: 'canceled' })
        .eq('id', companyId);
      if (error) throw error;
      await loadCompany();
      alert('Subscription canceled successfully');
    } catch (err) {
      alert('Failed to cancel subscription: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const reactivateSubscription = async () => {
    setActionLoading('reactivate');
    try {
      const { error } = await supabase
        .from('companies')
        .update({ subscription_status: 'active' })
        .eq('id', companyId);
      if (error) throw error;
      await loadCompany();
      alert('Subscription reactivated successfully');
    } catch (err) {
      alert('Failed to reactivate: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const applyCredit = async () => {
    const amount = parseFloat(creditAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid credit amount');
      return;
    }
    setActionLoading('credit');
    try {
      const { error } = await supabase
        .from('billing_credits')
        .insert({
          company_id: companyId,
          amount_cents: Math.round(amount * 100),
          reason: creditReason || 'Admin credit',
          applied_by: 'admin'
        });
      if (error) throw error;
      await logAdminAction(getAdminEmail(), 'manual_credit', 'company', companyId, company?.name, { amount, reason: creditReason || 'Admin credit' });
      setCreditAmount('');
      setCreditReason('');
      setTabData(prev => ({ ...prev, billing: null })); // Force reload
      loadTabData('billing');
      alert(`$${amount.toFixed(2)} credit applied successfully`);
    } catch (err) {
      alert('Failed to apply credit: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const toggleDemo = async () => {
    const enabling = !company.is_demo;
    const confirmMsg = enabling
      ? 'Enable demo mode? This will bypass payment, unlock all features, and use test Stripe keys.'
      : 'Disable demo mode? This will return the company to normal billing.';
    if (!window.confirm(confirmMsg)) return;
    setActionLoading('demo');
    try {
      const updateData = { is_demo: enabling };
      if (enabling) {
        updateData.subscription_status = 'active';
      }
      const { error } = await supabase
        .from('companies')
        .update(updateData)
        .eq('id', companyId);
      if (error) throw error;
      await logAdminAction(getAdminEmail(), enabling ? 'demo_enabled' : 'demo_disabled', 'company', companyId, company?.name);
      loadCompany();
      alert(enabling ? 'Demo mode enabled' : 'Demo mode disabled');
    } catch (err) {
      alert('Failed to toggle demo: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const deleteCompany = async () => {
    if (deleteConfirmText !== company.name) {
      alert('Company name does not match');
      return;
    }
    setActionLoading('delete');
    try {
      // Use the admin API endpoint to properly cancel Stripe and optionally purge owner
      const response = await fetch('https://workvanapp.com/api/admin-delete-company', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId,
          adminEmail: getAdminEmail(),
          purgeOwner
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Delete failed');
      }

      // Show detailed results
      const summary = [
        result.stripeSubscriptionCanceled ? '✓ Stripe subscription canceled' : '○ No Stripe subscription',
        result.companyDeleted ? '✓ Company deleted' : '✗ Company delete failed',
        purgeOwner ? (result.ownerPurged ? '✓ Owner account purged' : '○ Owner purge incomplete') : ''
      ].filter(Boolean).join('\n');

      await logAdminAction(getAdminEmail(), 'company_deleted', 'company', companyId, company?.name, { purgeOwner, result });
      alert(`Company deleted successfully!\n\n${summary}`);
      onBack();
    } catch (err) {
      alert('Failed to delete company: ' + err.message);
    } finally {
      setActionLoading(null);
      setShowDeleteConfirm(false);
      setPurgeOwner(false);
    }
  };

  const sendPasswordReset = async (email) => {
    setActionLoading(email);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://workvanapp.com/reset-password'
      });
      if (error) throw error;
      await logAdminAction(getAdminEmail(), 'password_reset', 'user', null, email, { company_id: companyId });
      alert(`Password reset email sent to ${email}`);
    } catch (err) {
      alert('Failed to send password reset: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const removeTeamMember = async (userId, userEmail) => {
    if (!window.confirm(`Remove ${userEmail} from this company?`)) return;
    setActionLoading(userId);
    try {
      const { error } = await supabase
        .from('user_companies')
        .delete()
        .eq('company_id', companyId)
        .eq('user_id', userId);
      if (error) throw error;
      await logAdminAction(getAdminEmail(), 'user_removed', 'user', userId, userEmail, { company_id: companyId, company_name: company?.name });
      await loadCompany();
      alert('Team member removed');
    } catch (err) {
      alert('Failed to remove team member: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
        Loading company...
      </div>
    );
  }

  if (!company) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
        Company not found.
        <button onClick={onBack} style={{
          display: 'block', margin: '1rem auto', padding: '0.5rem 1rem',
          background: '#2a2a2a', border: 'none', borderRadius: '6px', color: '#e0e0e0', cursor: 'pointer'
        }}>
          Back to Companies
        </button>
      </div>
    );
  }

  const statusColor = {
    active: '#4ecca3',
    trialing: '#f7931e',
    canceled: '#e74c3c',
    past_due: '#e74c3c',
    incomplete: '#888'
  };
  const sColor = statusColor[company.subscription_status] || '#888';
  const counts = tabData._counts || {};

  return (
    <div style={{ padding: '2rem 3rem' }}>
      {/* Back button */}
      <button
        onClick={onBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 0.75rem',
          background: 'transparent',
          border: '1px solid #2a2a2a',
          borderRadius: '6px',
          color: '#888',
          cursor: 'pointer',
          marginBottom: '1.5rem',
          fontSize: '0.85rem'
        }}
      >
        <ArrowLeft size={16} />
        Back to Companies
      </button>

      {/* Company Header */}
      <div style={{
        background: '#1a1a1a',
        borderRadius: '12px',
        border: '1px solid #2a2a2a',
        padding: '1.5rem 2rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: '700', color: '#e0e0e0', marginBottom: '0.5rem' }}>
              {company.name}
            </h1>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{
                padding: '0.25rem 0.75rem',
                background: `${sColor}20`,
                color: sColor,
                borderRadius: '4px',
                fontSize: '0.8rem',
                fontWeight: '600',
                textTransform: 'capitalize'
              }}>
                {company.subscription_status}
              </span>
              {company.tier && (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  background: '#3498db20',
                  color: '#3498db',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  {company.tier}
                </span>
              )}
              <span style={{ color: '#888', fontSize: '0.85rem' }}>
                <Calendar size={14} style={{ marginRight: '0.25rem', verticalAlign: 'middle' }} />
                Created {formatDate(company.created_at)}
              </span>
              {company.owner && (
                <span style={{ color: '#888', fontSize: '0.85rem' }}>
                  <Mail size={14} style={{ marginRight: '0.25rem', verticalAlign: 'middle' }} />
                  {company.owner.email}
                </span>
              )}
            </div>
            <div style={{ color: '#666', fontSize: '0.75rem', fontFamily: 'monospace', marginTop: '0.5rem' }}>
              ID: {company.id}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            {company.subscription_status === 'active' && (
              <div style={{ color: '#4ecca3', fontWeight: '700', fontSize: '1.25rem', fontFamily: '"JetBrains Mono", monospace' }}>
                {formatCurrency(getMonthlyRevenue(company.tier, company.billing_interval))}/mo
              </div>
            )}
            <div style={{ color: '#888', fontSize: '0.8rem' }}>
              {company.members.length} team member{company.members.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.25rem',
        marginBottom: '1.5rem',
        borderBottom: '1px solid #2a2a2a',
        overflowX: 'auto'
      }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const isDanger = tab.id === 'danger';
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                background: 'transparent',
                border: 'none',
                borderBottom: isActive ? `2px solid ${isDanger ? '#e74c3c' : '#ff6b35'}` : '2px solid transparent',
                color: isActive ? (isDanger ? '#e74c3c' : '#ff6b35') : '#888',
                cursor: 'pointer',
                fontWeight: isActive ? '600' : '400',
                fontSize: '0.9rem',
                whiteSpace: 'nowrap'
              }}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && <OverviewTab company={company} counts={counts} />}
        {activeTab === 'billing' && (
          <BillingTab
            company={company}
            data={tabData.billing}
            loading={tabLoading.billing}
            creditAmount={creditAmount}
            setCreditAmount={setCreditAmount}
            creditReason={creditReason}
            setCreditReason={setCreditReason}
            applyCredit={applyCredit}
            cancelSubscription={cancelSubscription}
            reactivateSubscription={reactivateSubscription}
            toggleDemo={toggleDemo}
            actionLoading={actionLoading}
          />
        )}
        {activeTab === 'team' && (
          <TeamTab
            members={company.members}
            sendPasswordReset={sendPasswordReset}
            removeTeamMember={removeTeamMember}
            actionLoading={actionLoading}
            companyId={companyId}
          />
        )}
        {activeTab === 'customers' && <CustomersTab data={tabData.customers} loading={tabLoading.customers} />}
        {activeTab === 'jobs' && <JobsTab data={tabData.jobs} loading={tabLoading.jobs} />}
        {activeTab === 'invoices' && <InvoicesTab data={tabData.invoices} loading={tabLoading.invoices} />}
        {activeTab === 'activity' && <ActivityTab data={tabData.activity} loading={tabLoading.activity} />}
        {activeTab === 'danger' && (
          <DangerTab
            company={company}
            showDeleteConfirm={showDeleteConfirm}
            setShowDeleteConfirm={setShowDeleteConfirm}
            deleteConfirmText={deleteConfirmText}
            setDeleteConfirmText={setDeleteConfirmText}
            purgeOwner={purgeOwner}
            setPurgeOwner={setPurgeOwner}
            deleteCompany={deleteCompany}
            actionLoading={actionLoading}
          />
        )}
      </div>
    </div>
  );
}

// ---- Tab Components ----

function OverviewTab({ company, counts }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
      <div style={{ background: '#1a1a1a', borderRadius: '12px', border: '1px solid #2a2a2a', padding: '1.5rem' }}>
        <h3 style={{ color: '#e0e0e0', fontWeight: '600', marginBottom: '1rem', fontSize: '1rem' }}>Company Details</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <DetailRow label="Company Name" value={company.name} />
          <DetailRow label="Subscription" value={company.subscription_status} />
          <DetailRow label="Plan" value={company.tier?.toUpperCase() || '-'} />
          <DetailRow label="Billing" value={company.billing_interval || '-'} />
          <DetailRow label="Stripe Customer" value={company.stripe_customer_id || 'None'} mono />
          <DetailRow label="Stripe Connect" value={company.stripe_connect_id || 'None'} mono />
          <DetailRow label="Created" value={formatDate(company.created_at)} />
        </div>
      </div>

      <div style={{ background: '#1a1a1a', borderRadius: '12px', border: '1px solid #2a2a2a', padding: '1.5rem' }}>
        <h3 style={{ color: '#e0e0e0', fontWeight: '600', marginBottom: '1rem', fontSize: '1rem' }}>Activity Summary</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <CountCard label="Customers" count={counts.customers} color="#3498db" />
          <CountCard label="Jobs" count={counts.jobs} color="#4ecca3" />
          <CountCard label="Quotes" count={counts.quotes} color="#9b59b6" />
          <CountCard label="Invoices" count={counts.invoices} color="#e74c3c" />
        </div>
      </div>
    </div>
  );
}

function BillingTab({ company, data, loading, creditAmount, setCreditAmount, creditReason, setCreditReason, applyCredit, cancelSubscription, reactivateSubscription, toggleDemo, actionLoading }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
      {/* Demo Account Toggle */}
      <div style={{ background: '#1a1a1a', borderRadius: '12px', border: company.is_demo ? '1px solid #ff6b35' : '1px solid #2a2a2a', padding: '1.5rem' }}>
        <h3 style={{ color: '#e0e0e0', fontWeight: '600', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Demo Account
          {company.is_demo && (
            <span style={{ background: '#ff6b35', color: '#fff', padding: '0.1rem 0.5rem', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 'bold' }}>ACTIVE</span>
          )}
        </h3>
        <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '1rem' }}>
          Demo accounts bypass payment, unlock all features, and use test Stripe keys.
        </p>
        <button
          onClick={toggleDemo}
          disabled={actionLoading === 'demo'}
          style={{
            padding: '0.75rem 1rem',
            background: company.is_demo ? '#e74c3c20' : '#ff6b3520',
            border: `1px solid ${company.is_demo ? '#e74c3c' : '#ff6b35'}`,
            borderRadius: '8px',
            color: company.is_demo ? '#e74c3c' : '#ff6b35',
            cursor: 'pointer',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          {actionLoading === 'demo' ? 'Updating...' : company.is_demo ? 'Disable Demo Mode' : 'Enable Demo Mode'}
        </button>
      </div>

      {/* Subscription Actions */}
      <div style={{ background: '#1a1a1a', borderRadius: '12px', border: '1px solid #2a2a2a', padding: '1.5rem' }}>
        <h3 style={{ color: '#e0e0e0', fontWeight: '600', marginBottom: '1rem' }}>Subscription</h3>
        <div style={{ marginBottom: '1rem' }}>
          <DetailRow label="Status" value={company.subscription_status} />
          <DetailRow label="Plan" value={company.tier || '-'} />
          <DetailRow label="Billing" value={company.billing_interval || '-'} />
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {company.subscription_status === 'active' ? (
            <button
              onClick={cancelSubscription}
              disabled={actionLoading === 'cancel'}
              style={{
                padding: '0.75rem 1rem',
                background: '#e74c3c20',
                border: '1px solid #e74c3c',
                borderRadius: '8px',
                color: '#e74c3c',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Ban size={16} />
              {actionLoading === 'cancel' ? 'Canceling...' : 'Cancel Subscription'}
            </button>
          ) : (
            <button
              onClick={reactivateSubscription}
              disabled={actionLoading === 'reactivate'}
              style={{
                padding: '0.75rem 1rem',
                background: '#4ecca320',
                border: '1px solid #4ecca3',
                borderRadius: '8px',
                color: '#4ecca3',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Check size={16} />
              {actionLoading === 'reactivate' ? 'Reactivating...' : 'Reactivate'}
            </button>
          )}
        </div>
      </div>

      {/* Apply Credit */}
      <div style={{ background: '#1a1a1a', borderRadius: '12px', border: '1px solid #2a2a2a', padding: '1.5rem' }}>
        <h3 style={{ color: '#e0e0e0', fontWeight: '600', marginBottom: '1rem' }}>Apply Billing Credit</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <input
            type="number"
            placeholder="Amount ($)"
            value={creditAmount}
            onChange={(e) => setCreditAmount(e.target.value)}
            style={{
              padding: '0.75rem',
              background: '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              color: '#e0e0e0'
            }}
          />
          <input
            type="text"
            placeholder="Reason (optional)"
            value={creditReason}
            onChange={(e) => setCreditReason(e.target.value)}
            style={{
              padding: '0.75rem',
              background: '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              color: '#e0e0e0'
            }}
          />
          <button
            onClick={applyCredit}
            disabled={actionLoading === 'credit'}
            style={{
              padding: '0.75rem 1rem',
              background: '#ff6b35',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            <DollarSign size={16} />
            {actionLoading === 'credit' ? 'Applying...' : 'Apply Credit'}
          </button>
        </div>
      </div>

      {/* Credit History */}
      <div style={{ background: '#1a1a1a', borderRadius: '12px', border: '1px solid #2a2a2a', padding: '1.5rem', gridColumn: '1 / -1' }}>
        <h3 style={{ color: '#e0e0e0', fontWeight: '600', marginBottom: '1rem' }}>Credit History</h3>
        {loading ? (
          <div style={{ color: '#888' }}>Loading...</div>
        ) : data?.credits?.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                <th style={tabThStyle}>Date</th>
                <th style={tabThStyle}>Amount</th>
                <th style={tabThStyle}>Reason</th>
                <th style={tabThStyle}>Applied By</th>
              </tr>
            </thead>
            <tbody>
              {data.credits.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid #2a2a2a' }}>
                  <td style={tabTdStyle}>{formatDate(c.created_at)}</td>
                  <td style={{ ...tabTdStyle, color: '#4ecca3', fontFamily: 'monospace' }}>${(c.amount_cents / 100).toFixed(2)}</td>
                  <td style={tabTdStyle}>{c.reason || '-'}</td>
                  <td style={tabTdStyle}>{c.applied_by || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ color: '#666' }}>No credits applied</div>
        )}
      </div>
    </div>
  );
}

function TeamTab({ members, sendPasswordReset, removeTeamMember, actionLoading, companyId }) {
  return (
    <div style={{ background: '#1a1a1a', borderRadius: '12px', border: '1px solid #2a2a2a', padding: '1.5rem' }}>
      <h3 style={{ color: '#e0e0e0', fontWeight: '600', marginBottom: '1rem' }}>Team Members ({members.length})</h3>
      {members.length === 0 ? (
        <div style={{ color: '#666' }}>No team members</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {members.map(m => (
            <div key={m.user_id} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem',
              background: '#242424',
              borderRadius: '8px',
              flexWrap: 'wrap',
              gap: '0.75rem'
            }}>
              <div>
                <div style={{ color: '#e0e0e0', fontWeight: '600' }}>{m.user?.full_name || 'Unnamed'}</div>
                <div style={{ color: '#888', fontSize: '0.85rem' }}>{m.user?.email || '-'}</div>
                <div style={{ color: '#666', fontSize: '0.75rem', fontFamily: 'monospace' }}>{m.user_id.substring(0, 8)}...</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{
                  padding: '0.25rem 0.5rem',
                  background: m.role === 'owner' ? '#ff6b3520' : '#3498db20',
                  color: m.role === 'owner' ? '#ff6b35' : '#3498db',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  textTransform: 'capitalize'
                }}>
                  {m.role}
                </span>
                <button
                  onClick={() => sendPasswordReset(m.user?.email)}
                  disabled={actionLoading === m.user?.email}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: '#2a2a2a',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#888',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    fontSize: '0.8rem'
                  }}
                >
                  <Key size={14} />
                  Reset PW
                </button>
                {m.role !== 'owner' && (
                  <button
                    onClick={() => removeTeamMember(m.user_id, m.user?.email)}
                    disabled={actionLoading === m.user_id}
                    style={{
                      padding: '0.5rem 0.75rem',
                      background: '#e74c3c20',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#e74c3c',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      fontSize: '0.8rem'
                    }}
                  >
                    <UserX size={14} />
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DangerTab({ company, showDeleteConfirm, setShowDeleteConfirm, deleteConfirmText, setDeleteConfirmText, purgeOwner, setPurgeOwner, deleteCompany, actionLoading }) {
  return (
    <div style={{ background: '#1a1a1a', borderRadius: '12px', border: '1px solid #e74c3c', padding: '1.5rem' }}>
      <h3 style={{ color: '#e74c3c', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <AlertTriangle size={20} />
        Danger Zone
      </h3>
      <p style={{ color: '#888', marginBottom: '1.5rem' }}>
        These actions are irreversible. Be careful.
      </p>

      {!showDeleteConfirm ? (
        <button
          onClick={() => setShowDeleteConfirm(true)}
          style={{
            padding: '0.75rem 1.5rem',
            background: '#e74c3c',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <Trash2 size={16} />
          Delete Company
        </button>
      ) : (
        <div style={{ background: '#2a2a2a', borderRadius: '8px', padding: '1.5rem', maxWidth: '500px' }}>
          <p style={{ color: '#e0e0e0', marginBottom: '1rem' }}>
            This will permanently delete <strong>{company.name}</strong> and all associated data including:
          </p>
          <ul style={{ color: '#888', marginBottom: '1rem', paddingLeft: '1.25rem' }}>
            <li>Stripe subscription (will be canceled)</li>
            <li>All team members</li>
            <li>All customers</li>
            <li>All jobs and quotes</li>
            <li>All invoices</li>
            <li>All products and SKUs</li>
          </ul>

          {/* Purge owner option */}
          <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem',
            padding: '1rem',
            background: '#1a1a1a',
            borderRadius: '8px',
            marginBottom: '1rem',
            cursor: 'pointer',
            border: purgeOwner ? '1px solid #e74c3c' : '1px solid #3a3a3a'
          }}>
            <input
              type="checkbox"
              checked={purgeOwner}
              onChange={(e) => setPurgeOwner(e.target.checked)}
              style={{ marginTop: '0.25rem' }}
            />
            <div>
              <div style={{ color: '#e0e0e0', fontWeight: '500', marginBottom: '0.25rem' }}>
                Also delete owner's account
              </div>
              <div style={{ color: '#888', fontSize: '0.85rem' }}>
                Allows them to sign up fresh with the same email. If unchecked, they can log in and create a new company.
              </div>
            </div>
          </label>

          <p style={{ color: '#e74c3c', marginBottom: '1rem' }}>
            Type "<strong>{company.name}</strong>" to confirm:
          </p>
          <input
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={company.name}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: '#1a1a1a',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              color: '#e0e0e0',
              marginBottom: '1rem'
            }}
          />
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); setPurgeOwner(false); }}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#3a3a3a',
                border: 'none',
                borderRadius: '8px',
                color: '#888',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={deleteCompany}
              disabled={deleteConfirmText !== company.name || actionLoading === 'delete'}
              style={{
                padding: '0.75rem 1.5rem',
                background: deleteConfirmText === company.name ? '#e74c3c' : '#666',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontWeight: '600',
                cursor: deleteConfirmText === company.name ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Trash2 size={16} />
              {actionLoading === 'delete' ? 'Deleting...' : 'Delete Forever'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ActivityTab({ data, loading }) {
  if (loading) return <TabLoading />;
  if (!data || data.length === 0) return <EmptyState label="activity" />;

  const typeColors = {
    company: '#ff6b35',
    team: '#3b82f6',
    job: '#10b981',
    invoice: '#f59e0b',
    admin: '#e74c3c',
  };

  const typeIcons = {
    company: Building2,
    team: Users,
    job: Briefcase,
    invoice: Receipt,
    admin: Shield,
  };

  const getRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
    return `${Math.floor(diffDays / 365)}y ago`;
  };

  const displayEvents = data.slice(0, 50);

  return (
    <div style={{ background: '#1a1a1a', borderRadius: '12px', border: '1px solid #2a2a2a', padding: '1.5rem' }}>
      <h3 style={{ color: '#e0e0e0', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Clock size={18} />
        Activity Timeline ({data.length} events)
      </h3>
      <div style={{ position: 'relative', paddingLeft: '2rem' }}>
        {/* Vertical timeline line */}
        <div style={{
          position: 'absolute',
          left: '7px',
          top: '8px',
          bottom: '8px',
          width: '2px',
          background: '#2a2a2a',
        }} />

        {displayEvents.map((event, i) => {
          const color = typeColors[event.type] || '#888';
          const IconComp = typeIcons[event.type] || Clock;
          return (
            <div key={i} style={{
              position: 'relative',
              paddingBottom: i === displayEvents.length - 1 ? 0 : '1.25rem',
            }}>
              {/* Dot on timeline */}
              <div style={{
                position: 'absolute',
                left: '-2rem',
                top: '2px',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                background: `${color}20`,
                border: `2px solid ${color}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: color }} />
              </div>

              {/* Event content */}
              <div style={{
                background: '#242424',
                borderRadius: '8px',
                padding: '0.75rem 1rem',
                border: '1px solid #2a2a2a',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <IconComp size={14} style={{ color, flexShrink: 0 }} />
                    <span style={{ color: '#e0e0e0', fontWeight: '600', fontSize: '0.9rem' }}>{event.title}</span>
                    <span style={{
                      padding: '0.1rem 0.4rem',
                      background: `${color}15`,
                      color,
                      borderRadius: '4px',
                      fontSize: '0.65rem',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                    }}>
                      {event.type}
                    </span>
                  </div>
                  <span style={{ color: '#666', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {getRelativeTime(event.date)}
                  </span>
                </div>
                {event.description && (
                  <div style={{ color: '#888', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                    {event.description}
                  </div>
                )}
                {event.date && (
                  <div style={{ color: '#555', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                    {formatDate(event.date)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {data.length > 50 && (
        <div style={{ textAlign: 'center', color: '#666', fontSize: '0.85rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #2a2a2a' }}>
          Showing 50 of {data.length} events
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: '#888', fontSize: '0.85rem' }}>{label}</span>
      <span style={{ color: '#e0e0e0', fontSize: '0.85rem', fontWeight: '500', textTransform: 'capitalize', fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</span>
    </div>
  );
}

function CountCard({ label, count, color }) {
  return (
    <div style={{ background: '#242424', borderRadius: '8px', padding: '0.75rem', textAlign: 'center' }}>
      <div style={{ fontSize: '1.5rem', fontWeight: '700', color, fontFamily: '"JetBrains Mono", monospace' }}>
        {count ?? '-'}
      </div>
      <div style={{ color: '#888', fontSize: '0.8rem' }}>{label}</div>
    </div>
  );
}

function TabLoading() {
  return <div style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>Loading...</div>;
}

function EmptyState({ label }) {
  return <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No {label} found</div>;
}

function CustomersTab({ data, loading }) {
  if (loading) return <TabLoading />;
  if (!data || data.length === 0) return <EmptyState label="customers" />;

  return (
    <div style={{ background: '#1a1a1a', borderRadius: '12px', border: '1px solid #2a2a2a', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
            <th style={tabThStyle}>Name</th>
            <th style={tabThStyle}>Phone</th>
            <th style={tabThStyle}>Email</th>
            <th style={tabThStyle}>Created</th>
          </tr>
        </thead>
        <tbody>
          {data.map(c => (
            <tr key={c.id} style={{ borderBottom: '1px solid #2a2a2a' }}>
              <td style={tabTdStyle}><span style={{ color: '#e0e0e0', fontWeight: '500' }}>{c.name}</span></td>
              <td style={tabTdStyle}>{c.phone || '-'}</td>
              <td style={tabTdStyle}>{c.email || '-'}</td>
              <td style={tabTdStyle}>{formatDate(c.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JobsTab({ data, loading }) {
  if (loading) return <TabLoading />;
  if (!data || data.length === 0) return <EmptyState label="jobs" />;

  const statusColors = { completed: '#4ecca3', scheduled: '#3498db', in_progress: '#f7931e', cancelled: '#e74c3c' };

  return (
    <div style={{ background: '#1a1a1a', borderRadius: '12px', border: '1px solid #2a2a2a', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
            <th style={tabThStyle}>Job #</th>
            <th style={tabThStyle}>Status</th>
            <th style={tabThStyle}>Customer</th>
            <th style={tabThStyle}>Scheduled</th>
            <th style={{ ...tabThStyle, textAlign: 'right' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {data.map(j => (
            <tr key={j.id} style={{ borderBottom: '1px solid #2a2a2a' }}>
              <td style={tabTdStyle}><span style={{ color: '#e0e0e0', fontFamily: 'monospace' }}>{j.job_number || '-'}</span></td>
              <td style={tabTdStyle}>
                <span style={{ padding: '0.2rem 0.5rem', background: `${statusColors[j.status] || '#888'}20`, color: statusColors[j.status] || '#888', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'capitalize' }}>
                  {j.status}
                </span>
              </td>
              <td style={tabTdStyle}>{j.customer_name}</td>
              <td style={tabTdStyle}>{formatDate(j.scheduled_date)}</td>
              <td style={{ ...tabTdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{j.total != null ? formatCurrency(j.total) : '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvoicesTab({ data, loading }) {
  if (loading) return <TabLoading />;
  if (!data || data.length === 0) return <EmptyState label="invoices" />;

  const statusColors = { paid: '#4ecca3', sent: '#3498db', draft: '#888', overdue: '#e74c3c', void: '#666' };

  return (
    <div style={{ background: '#1a1a1a', borderRadius: '12px', border: '1px solid #2a2a2a', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
            <th style={tabThStyle}>Invoice #</th>
            <th style={tabThStyle}>Status</th>
            <th style={tabThStyle}>Customer</th>
            <th style={{ ...tabThStyle, textAlign: 'right' }}>Amount</th>
            <th style={tabThStyle}>Due Date</th>
          </tr>
        </thead>
        <tbody>
          {data.map(inv => (
            <tr key={inv.id} style={{ borderBottom: '1px solid #2a2a2a' }}>
              <td style={tabTdStyle}><span style={{ color: '#e0e0e0', fontFamily: 'monospace' }}>{inv.invoice_number || '-'}</span></td>
              <td style={tabTdStyle}>
                <span style={{ padding: '0.2rem 0.5rem', background: `${statusColors[inv.status] || '#888'}20`, color: statusColors[inv.status] || '#888', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'capitalize' }}>
                  {inv.status}
                </span>
              </td>
              <td style={tabTdStyle}>{inv.customer_name}</td>
              <td style={{ ...tabTdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{inv.total != null ? formatCurrency(inv.total) : '-'}</td>
              <td style={tabTdStyle}>{formatDate(inv.due_date)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const tabThStyle = { textAlign: 'left', padding: '0.875rem 1rem', color: '#888', fontWeight: '500', fontSize: '0.85rem' };
const tabTdStyle = { padding: '0.75rem 1rem', color: '#888', fontSize: '0.9rem' };
